import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useApolloClient } from "@apollo/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { ACCEPT_INVITATION, GET_MY_DATA } from "@/queries/queries";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useHomeRoute } from "@/hooks/use-home-route";
import type { AcceptInvitationMutation, AcceptInvitationMutationVariables } from "@/generated/graphql";

const InvitationAccept = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const { route: homeRoute } = useHomeRoute();
  const client = useApolloClient();
  const [acceptInvitation, { loading }] = useMutation<AcceptInvitationMutation, AcceptInvitationMutationVariables>(ACCEPT_INVITATION);

  const [status, setStatus] = useState<'pending' | 'processing' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('invitation.invalidToken'));
    }
  }, [token, t]);

  const handleAcceptInvitation = async () => {
    if (!token) return;

    setStatus('processing');
    try {
      await acceptInvitation({
        variables: { token }
      });

      // Refresh user data to get updated team information
      await refreshUser();

      // Refetch MY_DATA query to update user's team membership data
      await client.refetchQueries({
        include: [GET_MY_DATA]
      });

      setStatus('success');
      toast({
        title: t('invitation.toast.acceptedSuccess'),
        description: t('invitation.toast.acceptedDesc'),
      });

      // Redirect to projects page after a short delay
      setTimeout(() => {
        navigate(homeRoute);
      }, 3000);

    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      setStatus('error');
      const errorMsg = error?.message || error?.graphQLErrors?.[0]?.message || t('invitation.toast.errorAccepting');
      setErrorMessage(errorMsg);

      toast({
        title: t('invitation.toast.errorAccepting'),
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'pending':
        return (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src="/images/autoclipper-logo-full.png"
                  alt="Autoclipper Logo"
                  className="h-12 w-auto"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-white">
                {t('invitation.teamInvitation')}
              </CardTitle>
              <CardDescription className="text-gray-400">
                {t('invitation.invitedToTeam')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center text-gray-300">
                <p className="mb-4">
                  {t('invitation.acceptingInvitation')}
                </p>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                    {t('invitation.accessTeamProjects')}
                  </li>
                </ul>
                <p className="text-sm text-gray-400 mb-4">
                  {t('invitation.ownerPermissions')}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleAcceptInvitation}
                  className="flex-1 bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('invitation.accepting')}
                    </>
                  ) : (
                    t('invitation.acceptInvitation')
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(homeRoute)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                  disabled={loading}
                >
                  {t('invitation.cancel')}
                </Button>
              </div>
            </CardContent>
          </>
        );

      case 'processing':
        return (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src="/images/autoclipper-logo-full.png"
                  alt="Autoclipper Logo"
                  className="h-12 w-auto"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-white">
                {t('invitation.processingInvitation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-green-400" />
              </div>
              <p className="text-gray-300">
                {t('invitation.acceptingTeamInvitation')}
              </p>
            </CardContent>
          </>
        );

      case 'success':
        return (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src="/images/autoclipper-logo-full.png"
                  alt="Autoclipper Logo"
                  className="h-12 w-auto"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-white">
                {t('invitation.invitationAccepted')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="h-12 w-12 text-green-400" />
              </div>
              <div className="space-y-2">
                <p className="text-gray-300 font-medium">
                  {t('invitation.welcomeToTeam')}
                </p>
                <p className="text-gray-400 text-sm">
                  {t('invitation.redirectingToProjects')}
                </p>
              </div>
            </CardContent>
          </>
        );

      case 'error':
        return (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src="/images/autoclipper-logo-full.png"
                  alt="Autoclipper Logo"
                  className="h-12 w-auto"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-white">
                {t('invitation.invitationError')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div className="flex justify-center">
                <XCircle className="h-12 w-12 text-red-400" />
              </div>
              <div className="space-y-2">
                <p className="text-gray-300 font-medium">
                  {t('invitation.couldNotAccept')}
                </p>
                <p className="text-gray-400 text-sm">
                  {errorMessage}
                </p>
              </div>
              <Button
                onClick={() => navigate(homeRoute)}
                className="bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold"
              >
                {t('invitation.backToProjects')}
              </Button>
            </CardContent>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        {renderContent()}
      </Card>
    </div>
  );
};

export default InvitationAccept;
